import type { Optional } from '@festina-life/core'
import {
  type editor,
  type IPosition,
  type IRange,
  languages,
  type Position,
} from 'monaco-editor'

import {
  type FPlusTextMapper,
  type LanguageWorkerAccessor,
  nullSafeTextMapper,
  type WorkerMapping,
} from '../worker/api'
import type { SuggestionResult } from '../worker/contracts/SuggestionResult'
import { ModelContextFinder } from './ModelContextFinder'

const CompletionItemTag = languages.CompletionItemTag

export class FPlusSuggestionProvider<T>
  implements languages.CompletionItemProvider
{
  triggerCharacters: string[]
  private worker: LanguageWorkerAccessor<T>
  private asWorkerInput: WorkerMapping<T>

  constructor(
    worker: LanguageWorkerAccessor<T>,
    asWorkerInput: WorkerMapping<T>,
  ) {
    this.worker = worker
    this.asWorkerInput = asWorkerInput
    const alphabet = 'abcdefghijklmnopqrstuvwxyz'
    this.triggerCharacters = ' .1234567890'
      .split('')
      .concat(alphabet.toLowerCase().split(''))
      .concat(alphabet.toUpperCase().split(''))
  }

  provideCompletionItems(
    model: editor.ITextModel,
    position: IPosition,
  ): languages.ProviderResult<languages.CompletionList> {
    return this.autoComplete(model, position)
  }

  private async autoComplete(
    model: editor.ITextModel,
    position: IPosition,
  ): Promise<languages.CompletionList | null> {
    const worker = await this.worker(model.uri)

    if (!worker) {
      return null
    }

    const replaceRange = ModelContextFinder.findFocusedWord(
      model,
      position as Position,
    )
    const [, endPosition] = replaceRange.asPositions()

    const input = await this.asWorkerInput({ model, position: endPosition })

    if (!input?.data) {
      return null
    }

    const completionList = await worker.suggestion(input.data)

    return this.resolveCompletionList(
      completionList,
      replaceRange,
      nullSafeTextMapper(input),
    )
  }

  private resolveCompletionList(
    completionList: SuggestionResult,
    range: IRange,
    textMapper: FPlusTextMapper,
  ): languages.CompletionList | null {
    return {
      suggestions: completionList.items.map((e) => {
        const kind = this.resolveKind(e.kind)
        const isMethod =
          kind === languages.CompletionItemKind.Method ||
          kind === languages.CompletionItemKind.Function
        const tags = e.tags
          ?.map((tag) =>
            tag === 'deprecated' ? CompletionItemTag.Deprecated : null,
          )
          .filter((v) => v !== null)
        const data: languages.CompletionItem = {
          label: {
            label: e.label?.label ?? '',
            detail: e.label?.detail ?? '',
            description: e.label?.description ?? '',
          },
          kind,
          documentation: {
            value: textMapper(e.documentation) ?? '',
            isTrusted: false,
            supportThemeIcons: true,
            supportHtml: false,
          },
          detail: e.typeDetail ?? undefined,
          insertText: e.insertText ?? '',
          sortText: e.sortText ?? undefined,
          tags,
          range,
          command: isMethod
            ? {
                id: 'editor.action.triggerParameterHints',
                title: 'parameterHelp',
              }
            : undefined,
          insertTextRules: isMethod
            ? languages.CompletionItemInsertTextRule.InsertAsSnippet
            : undefined,
        }

        return data
      }),
    }
  }

  private resolveKind(kind: Optional<string>): number {
    // oxlint-disable-next-line typescript/switch-exhaustiveness-check - it's a string and not a union
    switch (kind) {
      case 'method':
        return languages.CompletionItemKind.Method
      case 'function':
        return languages.CompletionItemKind.Function
      case 'property':
        return languages.CompletionItemKind.Property
      case 'snippet':
        return languages.CompletionItemKind.Snippet
      case 'interface':
        return languages.CompletionItemKind.Interface
      case undefined: {
        throw new Error('Not implemented yet: undefined case')
      }
      case null: {
        throw new Error('Not implemented yet: null case')
      }
      default:
        return languages.CompletionItemKind.Variable
    }
  }
}
